import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { skillsApi } from '../../utils/api';
import WizardLayout from '../../components/builder/WizardLayout';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import GlassCard from '../../components/GlassCard';
import ReactMarkdown from 'react-markdown';
import { 
  Download, Package, Eye, Beaker, CheckCircle, Check, Loader2, 
  Globe, Lock, Link2, FileText, Zap, BookOpen, GitBranch, Link, Lightbulb, Circle, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const Step3Publish = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [skillInfo, setSkillInfo] = useState(null);
  const [visibility, setVisibility] = useState('PUBLIC');
  const [state, setState] = useState('checking'); // 'checking', 'generating', 'complete', 'error'
  const [progress, setProgress] = useState({ current: 0, total: 7, fileName: '', message: '' });
  const [error, setError] = useState(null);
  
  // Package data
  const [packageInfo, setPackageInfo] = useState(null);
  const [skillContent, setSkillContent] = useState(null);
  
  const generationStartedRef = useRef(false);
  const abortControllerRef = useRef(new AbortController());

  useEffect(() => {
    // Check if package already exists, if not auto-start generation
    console.log('🔍 STEP 3: Checking if package exists for skill:', id);
    checkAndGenerate();
  }, [id]);

  const checkAndGenerate = async () => {
    try {
      const response = await skillsApi.get(id);
      const skill = response.data;
      setSkillInfo(skill);
      
      console.log('🔍 STEP 3: Skill loaded, checking package status...');
      
      if (skill.bundled_resources && skill.package_zip) {
        console.log('✅ STEP 3: Package already exists, navigating to Step 4');
        // Package already exists, go directly to Step 4
        navigate(`/builder/${id}/step-4`);
      } else if (!generationStartedRef.current) {
        // Package doesn't exist, auto-start generation
        console.log('🚀 STEP 3: No package found, auto-starting generation...');
        generationStartedRef.current = true;
        setState('generating');
        await handleStartGeneration();
      }
    } catch (error) {
      console.error('❌ STEP 3: Failed to load skill:', error);
      setState('error');
      setError(error.message || 'Failed to load skill');
    }
  };

  const loadPackageData = (skill) => {
    // Extract package info
    const slug = skill.slug || id;
    const packageMetadata = skill.package_metadata || {};
    const fileSizes = packageMetadata.file_sizes || {};
    
    setPackageInfo({
      slug: slug,
      zipUrl: `${process.env.REACT_APP_BACKEND_URL}/api/skills/${id}/download`,
      zipSize: fileSizes.total_package || 0,
      files: [
        { name: `${slug}.skill`, size: fileSizes.core || 0 },
        { name: `${slug}-prompt-library.md`, size: fileSizes.prompt_library || 0 },
        { name: `${slug}-usage-guide.md`, size: fileSizes.usage_guide || 0 },
        { name: `${slug}-decision-guide.md`, size: fileSizes.decision_guide || 0 }
      ]
    });
    
    // Extract skill content from bundled_resources
    const bundled = skill.bundled_resources || {};
    setSkillContent({
      overview: bundled.overview?.content || '# Overview\n\nNo content available.',
      promptLibrary: bundled.prompt_library?.content || '# Prompt Library\n\nNo content available.',
      usageGuide: bundled.usage_guide?.content || '# Usage Guide\n\nNo content available.',
      decisionGuide: bundled.decision_guide?.content || '# Decision Guide\n\nNo content available.',
      useCases: bundled.use_cases?.content || '# Use Cases\n\nNo content available.'
    });
  };

  const handleStartGeneration = async () => {
    console.log('🚀 STEP 3: User clicked Start Generation button');
    setState('generating');
    setProgress({ current: 0, total: 5, fileName: '', message: 'Starting package generation...' });
    
    try {
      console.log(`🚀 STEP 3: Making POST request to /api/builder/generate-package/${id}`);
      
      // Simulate progress updates (5 items: 4 files + use cases)
      const files = [
        'SKILL.md (Main overview)',
        'prompt-library.md',
        'usage-guide.md',
        'decision-guide.md',
        'Use cases'
      ];

      const progressInterval = setInterval(() => {
        setProgress(prev => {
          // Cap at 4 out of 5 (80%) to never show 100% until actually complete
          if (prev.current >= 4) return prev;
          const newCurrent = prev.current + 1;
          return {
            current: newCurrent,
            total: 5,
            fileName: files[newCurrent - 1] || 'Finalizing...',
            message: `Generating ${files[newCurrent - 1] || 'package'}...`
          };
        });
      }, 20000); // Update every 20 seconds (5 items = ~100 seconds)

      // Make the actual generation request with abort signal
      const packageResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/builder/generate-package/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        signal: abortControllerRef.current.signal
      });

      clearInterval(progressInterval);
      
      console.log('✅ STEP 3: Response received, parsing...');

      // Read the response ONCE
      let packageResult;
      try {
        packageResult = await packageResponse.json();
      } catch (jsonError) {
        console.error('❌ STEP 3: Failed to parse response:', jsonError);
        throw new Error('Invalid response from server');
      }

      // Check if request was successful
      if (!packageResponse.ok) {
        const errorMessage = packageResult?.detail || packageResult?.message || 'Package generation failed';
        throw new Error(errorMessage);
      }

      console.log('✅ STEP 3: Package generated successfully:', packageResult);

      // Set progress to 100% complete
      setProgress({ current: 5, total: 5, fileName: 'Complete!', message: 'Package generated successfully!' });

      // Wait a brief moment to show 100% completion
      await new Promise(resolve => setTimeout(resolve, 300));

      console.log('✅ STEP 3: Generation complete, navigating to Step 4...');
      
      // Navigate to Step 4
      navigate(`/builder/${id}/step-4`);
      
    } catch (error) {
      // Ignore abort errors
      if (error.name === 'AbortError') {
        console.log('🛑 STEP 3: Request aborted');
        return;
      }
      
      console.error('❌ STEP 3: Failed to generate package:', error);
      setState('error');
      setError(error.message || 'Failed to generate package');
      toast.error(error.message || 'Failed to generate package');
    }
  };

  const handlePublishAndDownload = async () => {
    setLoading(true);
    const progressToastId = toast.loading('Publishing skill...');
    
    try {
      // 1. Publish the skill
      const response = await skillsApi.publish(id, visibility);
      console.log('Publish response:', response);

      toast.success('🎉 Skill published successfully!', { id: progressToastId });
      
      // 2. Trigger download
      if (packageInfo && packageInfo.zipUrl) {
        const downloadLink = document.createElement('a');
        downloadLink.href = packageInfo.zipUrl;
        downloadLink.download = `${packageInfo.slug}.zip`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        
        toast.success('📥 Download started!');
      }
      
      // 3. Navigate to the skill detail page after brief delay
      setTimeout(() => {
        navigate(`/skills/${id}`);
      }, 1500);
      
    } catch (error) {
      console.error('Failed to publish', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to publish skill';
      toast.error(errorMessage, { id: progressToastId });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPackage = () => {
    if (!packageInfo) return;
    
    // Download the ZIP package
    window.location.href = packageInfo.zipUrl;
    toast.success('Downloading skill package...');
  };

  const handleBack = () => {
    navigate(`/builder/${id}/step-2`);
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 KB';
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  // Render based on state
  if (!skillInfo || state === 'checking') {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show error state
  if (state === 'error') {
    return (
      <WizardLayout currentStep={3} onBack={handleBack} showNext={false}>
        <div className="max-w-4xl mx-auto p-8">
          <GlassCard className="p-8 bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-700">
            <h2 className="text-2xl font-bold text-red-900 dark:text-red-100 mb-4">
              ❌ Generation Failed
            </h2>
            <p className="text-red-700 dark:text-red-300 mb-6">{error}</p>
            <Button
              onClick={() => {
                setState('generating');
                generatePackage();
              }}
              className="bg-gradient-to-r from-[#e57f3f] to-[#d96a2a] text-white px-6 py-3 rounded-lg font-bold"
            >
              Try Again
            </Button>
          </GlassCard>
        </div>
      </WizardLayout>
    );
  }

  // Show generation progress (inline, not modal)
  if (state === 'generating') {
    return (
      <WizardLayout currentStep={3} onBack={handleBack} showNext={false}>
        <div className="max-w-4xl mx-auto space-y-8 p-8">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-3xl font-manrope font-bold text-text-dark dark:text-dark-text mb-2">
              Step 3 of 4: Publish
            </h2>
          </div>

          {/* Generation Progress - INLINE */}
          <GlassCard className="p-8 bg-white dark:bg-dark-bg-secondary">
            <div className="flex items-center gap-3 mb-6">
              <Package className="h-8 w-8 text-[#e57f3f] animate-pulse" />
              <h2 className="text-2xl font-bold text-text-dark dark:text-dark-text">
                Generating Your Skill Package
              </h2>
            </div>

            <p className="text-text-gray dark:text-dark-text-secondary mb-6">
              Creating professional documentation with AI... This may take 1-2 minutes.
            </p>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-text-dark dark:text-dark-text">
                  Step {Math.min(progress.current, progress.total)} of {progress.total}
                </span>
                <span className="text-sm font-semibold text-primary">
                  {Math.min(100, Math.round((progress.current / progress.total) * 100))}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-[#e57f3f] to-[#d96a2a] h-3 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(100, (progress.current / progress.total) * 100)}%` }}
                />
              </div>
            </div>

            {/* Current File */}
            {progress.fileName && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                  <p className="font-semibold text-blue-900 dark:text-blue-100">{progress.message}</p>
                </div>
              </div>
            )}

            {/* Files Checklist */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-text-gray dark:text-dark-text-secondary mb-3">
                Package Contents:
              </p>
              {[
                'SKILL.md (Main overview)',
                'prompt-library.md',
                'usage-guide.md',
                'decision-guide.md',
                'Use cases'
              ].map((fileName, index) => {
                const isComplete = index < progress.current;
                const isCurrent = index === progress.current;

                return (
                  <div
                    key={fileName}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                      isComplete ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700' :
                      isCurrent ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700' :
                      'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {isComplete ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : isCurrent ? (
                      <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                    ) : (
                      <Circle className="h-5 w-5 text-gray-400" />
                    )}
                    <span className={`text-sm ${
                      isComplete ? 'text-green-900 dark:text-green-100 font-medium' :
                      isCurrent ? 'text-blue-900 dark:text-blue-100 font-medium' :
                      'text-text-gray dark:text-dark-text-secondary'
                    }`}>
                      {fileName}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Info */}
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
              <p className="text-sm text-blue-900 dark:text-blue-100 text-center">
                💡 Your skill is being created using 5 AI-powered generation steps for professional, production-quality documentation.
              </p>
            </div>
          </GlassCard>
        </div>
      </WizardLayout>
    );
  }

  // Show complete state with download/preview
  return (
    <WizardLayout
      currentStep={3}
      onBack={handleBack}
      onNext={handlePublishAndDownload}
      nextLabel={loading ? 'Publishing...' : '📦 Publish & Download'}
      nextDisabled={loading}
      showNext={true}
    >
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl font-manrope font-bold text-text-dark dark:text-dark-text mb-2">
            Step 3 of 3
          </h2>
          <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full mb-4">
            <div 
              className="bg-gradient-to-r from-[#e57f3f] to-[#d96a2a] h-2 rounded-full transition-all" 
              style={{ width: '100%' }} 
            />
          </div>
          <p className="text-primary font-semibold">100% Complete</p>
        </div>

        {/* Download ZIP Section */}
        {packageInfo && (
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-700 rounded-xl p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div>
                  <h2 className="text-2xl font-bold text-green-900 dark:text-green-100">
                    Your Skill Package is Ready
                  </h2>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Download and test before publishing
                  </p>
                </div>
              </div>
              <Button
                onClick={handleDownloadPackage}
                className="bg-gradient-to-r from-[#e57f3f] to-[#d96a2a] text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Download className="h-5 w-5" />
                Download ZIP
              </Button>
            </div>

            {/* Package Contents Breakdown */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2 text-gray-900 dark:text-gray-100">
                <Package className="h-5 w-5 text-[#e57f3f]" />
                Package Contents
              </h3>

              <div className="space-y-2 text-sm">
                {packageInfo.files.map((file, index) => (
                  <div key={index} className={`flex items-center justify-between py-2 ${index < packageInfo.files.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-400" />
                      <code className="font-mono text-gray-700 dark:text-gray-300">{file.name}</code>
                    </div>
                    <span className="text-gray-500 dark:text-gray-400">{formatBytes(file.size)}</span>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between font-semibold text-gray-900 dark:text-gray-100">
                <span>Total Package Size:</span>
                <span className="text-[#e57f3f]">{formatBytes(packageInfo.zipSize)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Preview Section with Interactive Tabs */}
        {skillContent && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="h-6 w-6 text-[#e57f3f]" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Preview Your Published Skill Page
              </h2>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-6">
              This is exactly how your skill will appear when published:
            </p>

            {/* Tabbed Interface with ACTUAL Content */}
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-5 mb-6 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
                <TabsTrigger value="overview" className="text-xs flex items-center gap-1 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-600">
                  <FileText className="h-3 w-3" />
                  <span className="hidden sm:inline">Overview</span>
                </TabsTrigger>
                <TabsTrigger value="prompt-library" className="text-xs flex items-center gap-1 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-600">
                  <Zap className="h-3 w-3" />
                  <span className="hidden sm:inline">Prompts</span>
                </TabsTrigger>
                <TabsTrigger value="usage-guide" className="text-xs flex items-center gap-1 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-600">
                  <FileText className="h-3 w-3" />
                  <span className="hidden sm:inline">Usage</span>
                </TabsTrigger>
                <TabsTrigger value="decision-guide" className="text-xs flex items-center gap-1 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-600">
                  <GitBranch className="h-3 w-3" />
                  <span className="hidden sm:inline">Decision</span>
                </TabsTrigger>
                <TabsTrigger value="use-cases" className="text-xs flex items-center gap-1 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-600">
                  <Lightbulb className="h-3 w-3" />
                  <span className="hidden sm:inline">Use Cases</span>
                </TabsTrigger>
              </TabsList>

              {/* Tab Content - ACTUAL GENERATED CONTENT */}
              <TabsContent value="overview" className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-gray-50 dark:bg-gray-900 max-h-96 overflow-y-auto">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{skillContent.overview}</ReactMarkdown>
                </div>
              </TabsContent>

              <TabsContent value="prompt-library" className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-gray-50 dark:bg-gray-900 max-h-96 overflow-y-auto">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{skillContent.promptLibrary}</ReactMarkdown>
                </div>
              </TabsContent>

              <TabsContent value="usage-guide" className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-gray-50 dark:bg-gray-900 max-h-96 overflow-y-auto">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{skillContent.usageGuide}</ReactMarkdown>
                </div>
              </TabsContent>

              <TabsContent value="decision-guide" className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-gray-50 dark:bg-gray-900 max-h-96 overflow-y-auto">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{skillContent.decisionGuide}</ReactMarkdown>
                </div>
              </TabsContent>

              <TabsContent value="use-cases" className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-gray-50 dark:bg-gray-900 max-h-96 overflow-y-auto">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{skillContent.useCases}</ReactMarkdown>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}

        {/* Testing Instructions with CORRECT Path */}
        {packageInfo && (
          <GlassCard className="p-8 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200 dark:border-blue-700">
            <div className="flex items-center gap-3 mb-4">
              <Beaker className="h-6 w-6 text-blue-600" />
              <h2 className="text-xl font-bold text-blue-900 dark:text-blue-100">
                Test Your Skill in Claude
              </h2>
            </div>

            <ol className="space-y-3 text-gray-700 dark:text-gray-300">
              <li className="flex gap-3">
                <span className="font-bold text-blue-600">1.</span>
                <span>Download the ZIP package above</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-blue-600">2.</span>
                <span>Go to claude.ai or Claude desktop app</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-blue-600">3.</span>
                <span><strong>Settings → Capabilities → Skills → Upload .ZIP or .Skill</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-blue-600">4.</span>
                <span>Test with real inputs and refine if needed</span>
              </li>
            </ol>
          </GlassCard>
        )}

        {/* Visibility Settings */}
        <GlassCard className="p-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            🌍 Visibility Settings
          </h2>
          <div className="space-y-3">
            {[
              { value: 'PRIVATE', label: 'Private (Only Me)', desc: 'Only you can see and use this skill', icon: Lock },
              { value: 'UNLISTED', label: 'Unlisted (Anyone with link)', desc: 'Not in public browse, but shareable', icon: Link2 },
              { value: 'PUBLIC', label: 'Public (Discoverable)', desc: 'Visible in Skills Browser', icon: Globe },
            ].map((option) => {
              const Icon = option.icon;
              return (
                <label 
                  key={option.value} 
                  className={`
                    flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all
                    ${visibility === option.value 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-primary/50'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="visibility"
                    value={option.value}
                    checked={visibility === option.value}
                    onChange={(e) => setVisibility(e.target.value)}
                    className="mt-1"
                  />
                  <Icon className="h-5 w-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{option.label}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{option.desc}</p>
                  </div>
                </label>
              );
            })}
          </div>
        </GlassCard>

        {/* Action Buttons - Always show when package is ready */}
        {packageInfo && (
          <div className="flex justify-between items-center pt-8">
            <Button
              onClick={handleBack}
              variant="ghost"
              className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-semibold"
            >
              ← Back
            </Button>

            <Button
              onClick={() => navigate(`/skills/${id}`)}
              className="bg-gradient-to-r from-[#e57f3f] to-[#d96a2a] text-white px-8 py-3 rounded-lg font-bold hover:shadow-lg transition-all"
            >
              View Published Skill →
            </Button>
          </div>
        )}
      </div>
    </WizardLayout>
  );
};

export default Step3Publish;
